<template>
    <div class="booking-engine-wrapper">
        <CustomSelect :max-height="500" :model-value="hotelIdSelected" @update:model-value="updateHotelSelected"
            :options="hotelSelectOptions" :placeholder="$filters.translate('all_hotels')" :allow-empty="false"
            v-if="hotels.length > 1"></CustomSelect>
        <div class="btn btn-white btn-single-hotel" v-else>
            <span v-if="hotelSelected" v-text="hotelSelected.title"></span>
            <input type="hidden" v-model="hotelIdSelected" />
        </div>
        <VueDatePicker range multi-calendars auto-apply six-weeks month-name-format="long" v-model="calendarDatesSelected"
            :min-range="1" :min-date="new Date()" :enable-time-picker="false" :locale="currentLang" position="left"
            :offset="0" @range-end="calendarInput" @open="calendarAppear" @closed="calendarDisappear">
            <template #trigger>
                <div class="btn btn-white btn-date">
                    <span
                        v-text="numNights > 0 ? (calendarRangeFormatted ?? $filters.translate('dates_sejour')) : $filters.translate('num_nights_minimum')"></span>
                    <span class="rate-from" v-if="hotelSelected" v-html="$filters.translate('rate_from')"></span>
                </div>
            </template>
            <template #day="{ day, date }">
                <span>{{ day }}</span>
                <span class="rate" v-if="hotelSelected">
                    {{ getDayRate(date) ?? '-' }}
                </span>
            </template>
            <template #arrow-left>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.6 39.8" xml:space="preserve">
                    <path fill="#333333" d="M20.6.7 1.4 19.9l19.2 19.2-.7.7L0 19.9 19.9 0l.7.7z" />
                </svg>
            </template>
            <template #arrow-right>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20.6 39.8" xml:space="preserve">
                    <path fill="#333333" d="M0 .7.7 0l19.9 19.9L.7 39.8l-.7-.7 19.2-19.2L0 .7z" />
                </svg>
            </template>
        </VueDatePicker>

        <div class="people-picker">
            <div class="btn btn-white btn-people-picker" @click="togglePeoplePicker"><span v-if="numPeople > 0"
                    v-text="numPeople + ' ' + (numPeople > 1 ? $filters.translate('num_personne_plural') : $filters.translate('num_personne_singular'))"></span><span
                    v-else v-html="$filters.translate('personnes')"></span></div>

            <div class="people-picker-inner" :class="{ 'shown': peoplePickerShown }">
                <div class="people-pick">
                    <label for="input-people-pick-adults" @click="selectInputRel"
                        v-html="$filters.translate('adultes')"></label>
                    <div class="btn btn-blue btn-update-people btn-less" @click="removePeople('adult')"><span>-</span></div>
                    <input id="input-people-pick-adults" type="text" class="numeric"
                        :value="bookingEngineParams.guests.adult.number" @input="updatePeople($event, 'adult')"
                        @keyup.up="addPeople('adult')" @keyup.down="removePeople('adult')"
                        @keypress="validKeyPressed($event)" @blur="checkInputPeople($event, 'adult')" />
                    <div class="btn btn-blue btn-update-people btn-more" @click="addPeople('adult')"><span>+</span></div>
                </div>
                <div class="people-pick">
                    <label for="input-people-pick-children" @click="selectInputRel"
                        v-html="$filters.translate('enfants')"></label>
                    <div class="btn btn-blue btn-update-people btn-less" @click="removePeople('child')"><span>-</span></div>
                    <input id="input-people-pick-children" type="text" class="numeric"
                        :value="bookingEngineParams.guests.child.number" @input="updatePeople($event, 'child')"
                        @keyup.up="addPeople('child')" @keyup.down="removePeople('child')"
                        @blur="checkInputPeople($event, 'child')" />
                    <div class="btn btn-blue btn-update-people btn-more" @click="addPeople('child')"><span>+</span></div>
                </div>
                <div class="people-pick">
                    <label for="input-people-pick-babys" @click="selectInputRel"
                        v-html="$filters.translate('bebes')"></label>
                    <div class="btn btn-blue btn-update-people btn-less" @click="removePeople('infant')"><span>-</span>
                    </div>
                    <input id="input-people-pick-babys" type="text" class="numeric"
                        :value="bookingEngineParams.guests.infant.number" @input="updatePeople($event, 'infant')"
                        @keyup.up="addPeople('infant')" @keyup.down="removePeople('infant')"
                        @blur="checkInputPeople($event, 'infant')" />
                    <div class="btn btn-blue btn-update-people btn-more" @click="addPeople('infant')"><span>+</span></div>
                </div>
                <div class="people-pick room-pick">
                    <label for="input-people-pick-rooms" @click="selectInputRel"
                        v-html="$filters.translate('chambres')"></label>
                    <div class="btn btn-blue btn-update-people btn-less" @click="removeRoom"><span>-</span></div>
                    <input id="input-people-pick-rooms" type="text" class="numeric" :value="bookingEngineParams.rooms"
                        @input="updateRoom" @keyup.up="addRoom" @keyup.down="removeRoom" @blur="checkInputRoom" />
                    <div class="btn btn-blue btn-update-people btn-more" @click="addRoom"><span>+</span></div>
                </div>
            </div>
        </div>

        <a :href="bookingLink" :class="['btn btn-blue btn-reserver', { 'disabled': numNights <= 0 }]" target="_blank">
            <span v-html="$filters.translate('reserver')"></span>
        </a>
    </div>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import dayjs from 'dayjs';
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';
import CustomSelect from '@/components/CustomSelect.vue';

const store = useStore();

const props = defineProps({
    hotels: {
        type: Array,
        required: true
    }
});

const formUpdated = ref(false);
const hotelIdSelected = ref('');
const calendarDatesSelected = ref([
    dayjs().format('YYYY-MM-DD'),
    dayjs().add(1, 'days').format('YYYY-MM-DD')
]);
const calendarInputOnce = ref(false);
const peopleMin = ref({
    adult: 1,
    child: 0,
    infant: 0
});
const peoplePickerShown = ref(false);

const configBookingLink = computed(() => store.state.configBookingLink);
const currentLang = computed(() => store.state.currentLang);
const bookingEngineParams = computed(() => store.state.bookingEngineParams);
const bookingEngineRates = computed(() => store.state.bookingEngineRates);
const bookingLang = computed(() => store.getters.bookingLang);

const calendarRangeFormatted = computed(() => {
    if (calendarInputOnce.value && calendarDatesSelected.value[0] && calendarDatesSelected.value[1]) {
        return dayjs(calendarDatesSelected.value[0]).format('DD/MM/YYYY') + ' - ' + dayjs(calendarDatesSelected.value[1]).format('DD/MM/YYYY');
    }

    return null;
});

const hotelSelectOptions = computed(() => {
    let options = [];

    if (props.hotels.length > 0) {
        props.hotels.forEach(hotel => {
            options.push({
                value: hotel.id,
                label: hotel.title
            });
        });
    }

    return options;
});

const hotelSelected = computed(() => {
    return props.hotels.length > 0 && hotelIdSelected.value !== '' ? props.hotels.filter(hotel => hotel.id === parseInt(hotelIdSelected.value, 10))[0] : null;
});

const numNights = computed(() => {
    if (calendarDatesSelected.value) {
        return Math.floor(dayjs(calendarDatesSelected.value[1]).diff(calendarDatesSelected.value[0], 'day'));
    }

    return 1;
});

const numPeople = computed(() => {
    return parseInt(bookingEngineParams.value.guests.adult.number, 10) + parseInt(bookingEngineParams.value.guests.child.number, 10) + parseInt(bookingEngineParams.value.guests.infant.number, 10);
});

const bookingLink = computed(() => {
    if (numNights.value <= 0) {
        return null;
    }

    let startDate = calendarDatesSelected.value ? dayjs(calendarDatesSelected.value[0]) : dayjs();
    let url = window.urlBaseBooking + (
        hotelIdSelected.value === ''
            ? (formUpdated.value ? window.bookingSettings['engine_path_form_updated'] : window.bookingSettings['engine_path'])
            : (formUpdated.value ? window.bookingSettings['engine_path_for_hotel_form_updated'] : window.bookingSettings['engine_path_for_hotel'])
    );

    return url +
        '?lingua_int=' + bookingLang.value +
        (hotelIdSelected.value === '' ? '&id_gruppo=' + configBookingLink.value.id_gruppo + '&dc_gruppo=' + configBookingLink.value.dc_gruppo : '') +
        '&id_stile=' + (hotelSelected.value !== null ? hotelSelected.value['booking_engine'].id_stile : configBookingLink.value.id_stile) +
        '&dc=' + (hotelSelected.value !== null ? hotelSelected.value['booking_engine'].dc : '') +
        '&id_albergo=' + (hotelSelected.value !== null ? hotelSelected.value['booking_engine'].id_albergo : '') +
        '&gg=' + startDate.format('D') +
        '&mm=' + startDate.format('M') +
        '&aa=' + startDate.format('YYYY') +
        '&notti_1=' + numNights.value +
        '&tot_adulti=' + bookingEngineParams.value.guests.adult.number +
        '&tot_bambini=' + (bookingEngineParams.value.guests.child.number + bookingEngineParams.value.guests.infant.number) +
        '&tot_camere=' + bookingEngineParams.value.rooms;
});

const calendarRates = computed(() => {
    let rates = [];

    if (bookingEngineRates.value !== null && hotelSelected.value !== null) {
        let i = 0;
        let datesRates = hotelSelected.value !== null ? bookingEngineRates.value[hotelSelected.value['booking_engine'].id_albergo] : bookingEngineRates.value['all'];
        for (let date in datesRates['dates']) {
            rates.push(
                {
                    key: 'rate.' + i,
                    dates: new Date(date),
                    customData: {
                        rate: Math.round(datesRates['dates'][date]['rates']) + '€'
                    },
                }
            );

            i++;
        }
    }

    return rates;
});

const updateGuestNumber = (payload) => store.dispatch('updateGuestNumber', payload);
const updateRoomNumber = (payload) => store.dispatch('updateRoomNumber', payload);
const updateBookingEngineHotels = (hotels) => store.dispatch('updateBookingEngineHotels', hotels);

onMounted(() => {
    document.addEventListener('click', function (e) {
        if (peoplePickerShown.value && e.target.closest('.people-picker') === null) {
            peoplePickerShown.value = false;
        }
    });

    // Hotel selected ?
    if (bookingEngineParams.value.hotels.length === 1) {
        hotelIdSelected.value = bookingEngineParams.value.hotels[0].id;
    }
    else if (props.hotels.length === 1) {
        hotelIdSelected.value = props.hotels[0].id;
    }

    updateHotelsInBookingEngine();
});

const getDayRate = (date) => {
    if (bookingEngineRates.value === null || hotelSelected.value === null) {
        return null;
    }

    let datesRates = bookingEngineRates.value[hotelSelected.value.booking_engine?.id_albergo]?.dates ?? bookingEngineRates.value?.all?.dates ?? null;

    const dateRates = datesRates[dayjs(date).format('YYYY-MM-DD')]?.rates ?? null;

    return dateRates !== null ? Math.round(dateRates) + '€' : null;
};

const calendarInput = (range) => {
    setFormUpdated();

    calendarInputOnce.value = true;
};

const calendarAppear = () => {
    peoplePickerShown.value = false;
    document.querySelector('.rate-from')?.classList.add('shown');
};

const calendarDisappear = () => {
    document.querySelector('.rate-from')?.classList.remove('shown');
};

const updateHotelSelected = (value) => {
    hotelIdSelected.value = value.value;
};

const togglePeoplePicker = () => {
    peoplePickerShown.value = !peoplePickerShown.value;
};

const selectInputRel = (e) => {
    document.querySelector('#' + e.target.getAttribute('for'))?.focus();
};

const validKeyPressed = (e) => {
    if (e.code === 'Backspace' || e.code === 'Tab' || e.code === 'Enter' || e.code === 'End' || e.code === 'Home' || e.code === 'ArrowLeft' || e.code === 'ArrowRight') {
        return;
    } else {
        e.preventDefault();
    }
}

const updatePeople = (e, type) => {
    updateGuestNumber({ type: type, number: e.target.value });
};

const removePeople = (type) => {
    setFormUpdated();
    if (bookingEngineParams.value.guests[type].number > peopleMin.value[type]) {
        updateGuestNumber({ type: type, number: bookingEngineParams.value.guests[type].number - 1 });
    }
};

const addPeople = (type) => {
    setFormUpdated();
    updateGuestNumber({ type: type, number: bookingEngineParams.value.guests[type].number + 1 });
};

const checkInputPeople = (e, type) => {
    let number = Math.max((parseInt(e.target.value, 10) || 0), peopleMin.value[type]);
    if (number !== bookingEngineParams.value.guests[type].number) {
        updateGuestNumber({ type: type, number: number });
    } else {
        updateGuestNumber({ type: type, number: number });
    }
};

const updateRoom = (e) => {
    updateRoomNumber({ number: e.target.value });
};

const removeRoom = () => {
    setFormUpdated();
    if (bookingEngineParams.value.rooms > 0) {
        updateRoomNumber({ number: bookingEngineParams.value.rooms - 1 });
    }
};

const addRoom = () => {
    setFormUpdated();
    updateRoomNumber({ number: bookingEngineParams.value.rooms + 1 });
};

const checkInputRoom = (e) => {
    let number = Math.max((parseInt(e.target.value, 10) || 0), 0);
    if (number !== bookingEngineParams.value.rooms) {
        updateRoomNumber({ number: number });
    } else {
        updateRoomNumber({ number: number });
    }
};

const setFormUpdated = () => {
    formUpdated.value = true;
};

const updateHotelsInBookingEngine = () => {
    let hotels = hotelSelected.value !== null
        ? [hotelSelected.value]
        : props.hotels.filter(hotel => hotel.id !== '');

    updateBookingEngineHotels(hotels);
};
</script>

<style lang="scss">
/* Calendar theming */
:root {
    --dp-font-family: 'HelveticaNeue Bold', "Helvetica Neue", sans-serif;
    --dp-border-radius: 0;
    --dp-cell-border-radius: 0;
    --dp-month-year-row-height: 60px;
    --dp-month-year-row-button-size: 28px;
    --dp-button-icon-height: 28px;
    --dp-cell-size: 42px;
    --dp-row-margin: 0;
    --dp-overlay-col-padding: 0;
    --dp-menu-padding: 20px 36px;
    --dp-font-size: 1.2rem;
    --dp-multi-calendars-spacing: 0;
}

.dp__theme_light {
    --dp-text-color: #333333;
    --dp-hover-color: var(--libertel-blue);
    --dp-hover-text-color: #ffffff;
    --dp-hover-icon-color: #959595;
    --dp-primary-color: var(--libertel-blue);
    --dp-primary-text-color: #ffffff;
    --dp-highlight-color: rgb(25 118 210 / 10%);
    --dp-range-between-dates-background-color: var(--libertel-blue-light);
    --dp-range-between-dates-text-color: var(--dp-primary-text-color);
}

.dp__main {
    width: auto;
    letter-spacing: 0.1rem;
}

.dp__menu_inner {
    gap: 8rem;
}

.dp__arrow_bottom {
    display: none;
}

.dp__inner_nav {
    transition: opacity 0.2s ease;

    &:hover {
        background-color: transparent;
        opacity: 0.5;
    }
}

.dp__month_year_wrap {
    justify-content: center;
    gap: 1rem;
}

.dp__month_year_select {
    width: auto;
    font-size: 2.4rem;
    letter-spacing: 0.1rem;
    text-transform: uppercase;

    &:hover {
        background-color: transparent;
        color: var(--libertel-blue);
    }
}

.dp__overlay_cell_disabled:hover {
    color: var(--dp-text-color);
}

.dp--arrow-btn-nav {
    position: absolute;
    left: 0;
}

.dp__instance_calendar+.dp__instance_calendar {
    .dp--arrow-btn-nav {
        left: auto;
        right: 0;
    }
}

.dp__overlay_action {
    display: none;
}

.dp__calendar_header_item {
    text-transform: uppercase;
    height: auto;
}

.dp__calendar_header_separator {
    display: none;
}

.dp__cell_inner {
    flex-direction: column;
    border: 0;

    .rate {
        font-size: 1.0rem;
        color: #a299da;
    }

    &.dp__range_start,
    &.dp__range_end,
    &.dp__range_between,
    &.dp__date_hover_end:hover {
        .rate {
            color: #fff;
        }
    }
}

@media screen and (min-width: 1120px) {
    .dp__menu_inner {
        gap: 10rem;
    }

    .dp__cell_inner {
        width: 4.8rem;
    }

    .booking-engine-wrapper .rate-from {
        left: 370px;
    }
}
</style>